import type { Draft, PayloadAction } from '@reduxjs/toolkit';

import type { AISidekickChatState, MessageActionResult } from './chatTypes';

/** Payload for `applyActionResults`. */
export type ApplyActionResultsPayload = {
  actionResults: ReadonlyArray<MessageActionResult>;
  /** Id of the answer the turn finalized, when it finalized one. */
  answerId?: string;
  /** Id of the answer created when the turn has none to carry the results. */
  fallbackMessageId: string;
  createdAt: string;
};

/**
 * Adds a turn's action results to the answer that turn finalized. Matching
 * by id rather than position keeps a retry's results off answers that came
 * after its question. A turn with no answer gets one that reads the results'
 * messages, so an action the backend ran is never silent.
 */
export const applyActionResultsReducer = (
  state: Draft<AISidekickChatState>,
  action: PayloadAction<ApplyActionResultsPayload>
): void => {
  const { actionResults, answerId, fallbackMessageId, createdAt } =
    action.payload;
  const answer =
    answerId === undefined
      ? undefined
      : state.messages.find(
          ({ id, role }) => id === answerId && role === 'assistant'
        );
  if (answer !== undefined) {
    answer.actionResults = [...(answer.actionResults ?? []), ...actionResults];
    return;
  }
  const content = actionResults
    .map(({ message }) => message.trim())
    .filter((message) => message !== '')
    .join('\n\n');
  if (content === '') {
    return;
  }
  state.messages.push({
    id: fallbackMessageId,
    conversationId: state.conversationId ?? '',
    role: 'assistant',
    content,
    createdAt,
    actionResults: [...actionResults],
    isLocalOnly: true,
    ...(state.answeringQuestionId !== null
      ? { sourceQuestionId: state.answeringQuestionId }
      : {}),
  });
  if (state.conversationMetadata !== null) {
    state.conversationMetadata.messageCount += 1;
  }
};
